import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BLUE_BUTTON, RED_BUTTON, WHITE } from "./baseModal/colours";
import {
  CONTENT_BLOCK,
  DATE_PICKER,
  SWITCH
} from "./baseModal/FieldTypes";
import BaseModal from "./baseModal/BaseModal";
import ManualBillingCycleModal from "./ManualBillingCycleModal";
import "react-datepicker/dist/react-datepicker.css";
import "./EditBillingCycleModal.css";

function formatBillingDay(day) {
  const lastTwo = day % 100;
  const suffix = lastTwo >= 11 && lastTwo <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th");
  return `${day}${suffix}`;
}

const calendarDate = value => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const dateValue = date => date && !Number.isNaN(date.getTime())
  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` : "";
const isAllowedDate = (date, options, timing) => {
  const value = dateValue(date);
  const minimum = timing === "after_next_payment" ? options?.minimumKeptPaymentDate : options?.minimumPaymentDate;
  return !!(value && options && minimum && date.getDate() <= 28 && value >= minimum && value <= options.maximumPaymentDate);
};

export const EditBillingCycleModal = ({ program, classification = "Program", onClose }) => {
  const [firstPaymentDate, setFirstPaymentDate] = useState(null);
  const [changeTiming, setChangeTiming] = useState("next_payment");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsVersion, setOptionsVersion] = useState(0);
  const endpoint = `${process.env.REACT_APP_API}/partners/billing-day-changes`;
  const supported = !!program.stripeSubscription;
  const selectedDate = dateValue(firstPaymentDate);
  const previewMatchesSelection = preview && preview.firstPaymentDate === selectedDate &&
    (preview.changeTiming || "next_payment") === changeTiming;

  useEffect(() => {
    let active = true;
    setOptionsLoading(true);
    setOptions(null);
    if (!supported) {
      setOptionsLoading(false);
      return () => { active = false; };
    }
    axios.post(`${endpoint}/options`, { classification, customerId: Number(program.id) },
      { ignore_global_lastPost: true }).then(response => {
      if (!response.data.success) throw new Error(response.data.message);
      if (active) setOptions(response.data.data);
    }).catch(cause => {
      if (active) setError(cause.response?.data?.message || cause.message || "Unable to load the current billing schedule.");
    }).finally(() => { if (active) setOptionsLoading(false); });
    return () => { active = false; };
  }, [classification, program.id, endpoint, optionsVersion, supported]);

  useEffect(() => {
    let active = true;
    setPreview(null);
    if (!selectedDate || !supported || !isAllowedDate(firstPaymentDate, options, changeTiming)) {
      setLoading(false);
      return () => { active = false; };
    }
    setError("");
    setLoading(true);
    axios.post(`${endpoint}/preview`, {
      classification, customerId: Number(program.id), firstPaymentDate: selectedDate, changeTiming
    }, { ignore_global_lastPost: true }).then(response => {
      if (!response.data.success) throw new Error(response.data.message);
      if (active) setPreview(response.data.data);
    }).catch(cause => {
      if (active) setError(cause.response?.data?.message || cause.message || "Unable to preview the billing change.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedDate, firstPaymentDate, options, changeTiming, classification, program.id, endpoint, supported, previewVersion]);

  const handleSubmit = async () => {
    if (!previewMatchesSelection || loading || sending || optionsLoading) return;
    setSending(true);
    setError("");
    let sent = false;
    try {
      const response = await axios.post(endpoint, {
        classification, customerId: Number(program.id), firstPaymentDate: selectedDate,
        changeTiming,
        previewKey: preview.previewKey
      }, { ignore_global_lastPost: true });
      if (!response.data.success) throw new Error(response.data.message);
      toast.success("Confirmation email sent. Billing stays unchanged until the parent confirms.");
      sent = true;
    } catch (cause) {
      setError(cause.response?.data?.message || cause.message || "Unable to send the confirmation email.");
      if (cause.response?.status === 409) {
        setPreview(null);
        setOptionsVersion(version => version + 1);
        setPreviewVersion(version => version + 1);
      }
    } finally {
      setSending(false);
    }
    if (sent) onClose();
  };

  const modalFields = [
    {
      type: SWITCH,
      data: {
        name: "Keep next payment unchanged",
        checked: changeTiming === "after_next_payment",
        handleClick: () => {
          const timing = changeTiming === "next_payment" ? "after_next_payment" : "next_payment";
          setChangeTiming(timing);
          if (firstPaymentDate && !isAllowedDate(firstPaymentDate, options, timing)) setFirstPaymentDate(null);
        },
        isDisabled: sending || !supported || optionsLoading || !options?.canKeepNextPayment,
        options: { color: "primary", inputProps: { "aria-label": "Keep next payment unchanged" } },
        info: options ? `Keep the payment on ${options.currentNextPaymentDate}. The first payment on the new schedule must be after this date.` : "Keep the upcoming payment on its current date."
      }
    },
    {
      type: DATE_PICKER,
      data: {
        name: "First payment on the new schedule",
        value: firstPaymentDate,
        handleChange: date => {
          if (!date || isAllowedDate(date, options, changeTiming)) {
            setFirstPaymentDate(date);
            setError("");
          }
        },
        onChangeRaw: () => { setPreview(null); setFirstPaymentDate(null); },
        allowSameDay: true,
        popperProps: { strategy: "fixed" },
        placeholder: "Choose a date",
        customInput: <input aria-label="First payment on the new schedule" />,
        dateFormat: "MMMM d, yyyy",
        minDate: calendarDate(changeTiming === "after_next_payment" ? options?.minimumKeptPaymentDate : options?.minimumPaymentDate),
        maxDate: calendarDate(options?.maximumPaymentDate),
        filterDate: date => isAllowedDate(date, options, changeTiming),
        isDisabled: sending || !supported || optionsLoading || !options,
        info: "Choose the 1st–28th, at least 24 hours ahead and within two years. Payments will repeat monthly on the selected day."
      }
    },
    {
      type: CONTENT_BLOCK,
      data: {
        content: supported
          ? "We will email the paying parent a link to review and confirm this recurring change. The regular monthly price stays the same."
          : "Billing-day confirmation is available for monthly automatic payments. Contact billing support for manual invoices."
      }
    }
  ];

  if (optionsLoading) modalFields.push({ type: CONTENT_BLOCK, data: { content: "Loading the current billing schedule…" } });
  if (options) modalFields.push({ type: CONTENT_BLOCK, data: { name: "Current schedule", content: `${formatBillingDay(options.currentBillingDay)} of each month. Next payment: ${options.currentNextPaymentDate}.` } });
  if (changeTiming === "after_next_payment" && options) modalFields.push({ type: CONTENT_BLOCK, data: { content: `The payment on ${options.currentNextPaymentDate} will stay unchanged. Choose a later date in the calendar.` } });
  if (loading) modalFields.push({ type: CONTENT_BLOCK, data: { content: "Checking the selected payment date…" } });
  if (preview) {
    if (preview.keptPaymentDate) modalFields.push({ type: CONTENT_BLOCK, data: { name: "Next payment stays unchanged", content: preview.keptPaymentDate } });
    modalFields.push({ type: CONTENT_BLOCK, data: { name: "Proposed schedule", content: `${formatBillingDay(preview.billingDay)} of each month. Next payments: ${[preview.nextPaymentDate, ...preview.followingPaymentDates].join("; ")} (${preview.timezone}).` } });
    modalFields.push({ type: CONTENT_BLOCK, data: { name: "Confirmation email", content: preview.parentEmail } });
    modalFields.push({ type: CONTENT_BLOCK, data: { content: preview.transitionNotice } });
  }
  if (error) modalFields.push({ type: CONTENT_BLOCK, data: { content: error, textStyles: { color: "#b42318" } } });

  const modalButtons = [
    {
      name: sending ? "Sending…" : "Send confirmation email",
      buttonColour: BLUE_BUTTON,
      textColour: WHITE,
      handleClick: handleSubmit,
      isDisabled: !previewMatchesSelection || loading || sending || optionsLoading
    },
    {
      name: "Cancel",
      buttonColour: RED_BUTTON,
      textColour: WHITE,
      handleClick: onClose,
      isDisabled: sending
    }
  ];

  return (
    <div className="billing-day-change-modal"><BaseModal
      title="Change recurring billing day"
      fields={modalFields}
      buttons={modalButtons}
      handleClose={() => { if (!sending) onClose(); }}
      height="75vh"
      midSectionHeight="auto"
    /></div>
  );
};

export default function BillingCycleModal(props) {
  return props.program.stripeSubscription
    ? <EditBillingCycleModal {...props} />
    : <ManualBillingCycleModal {...props} />;
}
